import { HttpError, useOne, useParse, useTranslation } from "@refinedev/core";
import { isEmpty } from "lodash";
import { CreateEventStep, ISimpleEventDetail, ITicketsResponseData, Phase } from "interfaces";
import { SwitchButton } from "components/switchButton";

import { TicketPhase } from "./ticketPhase";
import { FullScreenLoading } from "components/fullScreenLoading";
import { CollapsibleSection } from "components/collapsibleSection";
import { Tickets } from "./tickets";
import { Link } from "react-router-dom";
import Button from "components/button";
import { endpointService } from "components/shared/services/endpointService";
import { Label } from "components/label";

export const ViewTicketSetup = ({
  eventDetailData,
}: {
  eventDetailData: ISimpleEventDetail;
}) => {
  const { translate } = useTranslation();
  const parse = useParse();
  const { params } = parse();

  const { data, isFetching } = useOne<ITicketsResponseData>({
    resource: endpointService.getEventEndpoint("getTickets", {
      eventId: params?.id ?? "",
    }),
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(params?.id && params?.step),
    } as any,
  });

  const { data: ticketPhaseData } = useOne<Phase[], HttpError>({
    resource: endpointService.getEventEndpoint("getPhases", {
      eventId: eventDetailData.id ?? "",
    }),
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!(params?.id && params?.step),
    } as any,
  });

  const ticketPhases = ticketPhaseData?.data ?? null;

  const ticketsData = data?.data ?? null;

  if (isFetching) {
    return <FullScreenLoading />;
  }

  return (
    <div className="flex flex-col h-full gap-y-2 sm:gap-y-4">
      <CollapsibleSection
        label={translate("eventSetup.phaseAndTicket.ticketPhases")}
        customSubLabel={
          <div className="text-sm">
            <div className="mt-2 text-sm flex flex-wrap items-center gap-x-4">
              <Label
                id="ticket-phases"
                name={translate("eventSetup.phaseAndTicket.togglePhaseLabel")}
                hint={translate("eventSetup.phaseAndTicket.togglePhaseNote")}
                className="m-0 font-normal"
              />
              <SwitchButton
                checked={!isEmpty(ticketPhases ?? [])}
                disabled={true}
                onChange={() => {}}
              />
            </div>
            <p className="mt-2">
              {translate("eventSetup.phaseAndTicket.phaseNote")}
            </p>
          </div>
        }
        hideArrow={isEmpty(ticketPhases)}
        hideContent={isEmpty(ticketPhases)}
        defaultOpen={false}
      >
        <TicketPhase
          eventDetailData={eventDetailData}
          ticketPhases={ticketPhases ?? []}
        />
      </CollapsibleSection>
      <CollapsibleSection
        label={translate("eventSetup.phaseAndTicket.individualTickets")}
        customSubLabel={
          <div className="text-sm">
            {!isEmpty(ticketPhases ?? []) ? (
              <div className="mt-2 text-sm flex flex-wrap items-center gap-x-4">
                <Label
                  id="public-all-ticket-phases"
                  name={translate(
                    "eventSetup.phaseAndTicket.publicAllTicketPhases"
                  )}
                  hint={translate(
                    "eventSetup.phaseAndTicket.publicAllTicketPhasesNote"
                  )}
                  className="m-0 font-normal"
                />
                <SwitchButton
                  checked={
                    eventDetailData.is_publish_all_ticket_phases ?? false
                  }
                  onChange={() => {}}
                  disabled
                />
              </div>
            ) : (
              <></>
            )}
          </div>
        }
        defaultOpen={false}
      >
        <Tickets
          eventDetailData={eventDetailData}
          ticketPhases={ticketPhases ?? []}
          tickets={ticketsData?.tickets ?? []}
        />
      </CollapsibleSection>
      <CollapsibleSection
        label={translate("eventSetup.phaseAndTicket.groupTickets")}
        customSubLabel={
          <div className="text-sm">
            <div className="mt-2 text-sm flex flex-wrap items-center gap-x-4">
              <Label
                id="sell-group-tickets"
                name={translate(
                  "eventSetup.phaseAndTicket.doYouSellGroupTickets"
                )}
                className="m-0 font-normal"
              />
              <SwitchButton
                checked={eventDetailData.is_payment_group}
                onChange={() => {}}
                disabled
              />
            </div>
            {!isEmpty(ticketPhases ?? []) ? (
              <div className="mt-4 text-sm flex flex-wrap items-center gap-x-4">
                <Label
                  id="public-all-group-ticket-phases"
                  name={translate(
                    "eventSetup.phaseAndTicket.publicAllGroupTicketPhases"
                  )}
                  hint={translate(
                    "eventSetup.phaseAndTicket.publicAllGroupTicketPhasesNote"
                  )}
                  className="m-0 font-normal"
                />
                <SwitchButton
                  checked={
                    eventDetailData.is_publish_all_ticket_phases_group ?? false
                  }
                  onChange={() => {}}
                  disabled
                />
              </div>
            ) : (
              <></>
            )}
          </div>
        }
        defaultOpen={false}
      >
        <Tickets
          eventDetailData={eventDetailData}
          ticketPhases={ticketPhases ?? []}
          tickets={ticketsData?.group_tickets ?? []}
        />
      </CollapsibleSection>
      <div
        className="w-full mt-auto px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2]"
        style={{ boxShadow: "rgba(0, 0, 0, 0.23) 0px -5px 5px -5px" }}
      >
        <div className="flex items-center justify-end gap-x-4">
          <Link to={`/events/sports/${params?.id}?step=${CreateEventStep.PARTICIPANT_TEMPLATE}`}>
            <Button className="min-w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
             {translate("button.back")}
            </Button>
          </Link>
          <Link
            to={`/events/sports/${params?.id}?step=${CreateEventStep.ADDITIONAL_SETUP}`}
          >
            <Button
              className={`min-w-20 disabled:opacity-70`}
              disabled={eventDetailData.current_step < params?.step}
            >
              {translate("button.next")}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};